const ALLOWED_BASE_PATH = '/help/';

/**
 * Markdownファイル読み込みエラー
 */
export class MarkdownLoadError extends Error {
  constructor(
    message: string,
    public readonly path: string,
    public readonly statusCode?: number
  ) {
    super(message);
    this.name = 'MarkdownLoadError';
  }
}

/**
 * Markdownキャッシュ（本番環境のみ）
 */
const markdownCache = new Map<string, string>();

/**
 * Markdownファイルを読み込む（セキュア実装）
 *
 * @param path - Markdownファイルのパス（例: '/help/getting-started/overview.md'）
 * @returns Markdown文字列
 *
 * セキュリティ対策:
 * - パストラバーサル対策（..を除去、/help/プレフィックス検証）
 * - Content-Typeチェック
 * - 本番環境ではキャッシング
 */
export async function loadMarkdown(path: string): Promise<string> {
  // キャッシュヒット
  if (markdownCache.has(path)) {
    return markdownCache.get(path)!;
  }

  try {
    // パストラバーサル対策
    const normalizedPath = path.replace(/\.\./g, '');
    if (!normalizedPath.startsWith(ALLOWED_BASE_PATH)) {
      throw new MarkdownLoadError('Invalid help path', path);
    }

    const response = await fetch(normalizedPath);
    if (!response.ok) {
      throw new MarkdownLoadError(
        `Failed to load help content`,
        normalizedPath,
        response.status
      );
    }

    // Content-Typeの検証
    const contentType = response.headers.get('content-type');
    if (contentType && !contentType.includes('text/markdown') && !contentType.includes('text/plain')) {
      console.warn('Unexpected content type:', contentType);
    }

    const content = await response.text();

    // キャッシュに保存（本番環境のみ）
    if (import.meta.env.PROD) {
      markdownCache.set(path, content);
    }

    return content;
  } catch (error) {
    console.error('Error loading markdown:', error);

    // 詳細なエラーメッセージ
    if (error instanceof MarkdownLoadError) {
      if (error.statusCode === 404) {
        return '# ページが見つかりません\n\nこのヘルプページは削除されたか、移動された可能性があります。';
      }
      return `# エラー\n\nヘルプコンテンツの読み込みに失敗しました。\n\nエラー詳細: ${error.message}`;
    }

    return '# エラー\n\n予期しないエラーが発生しました。しばらくしてから再度お試しください。';
  }
}

/**
 * Markdownキャッシュをクリア
 */
export function clearMarkdownCache(): void {
  markdownCache.clear();
}
